const isProduction = process.env.NODE_ENV === "production";
const trimTrailingSlash = (value?: string) => value?.replace(/\/+$/, "");
const configuredCoreAPI = trimTrailingSlash(process.env.REACT_APP_CORE_API_URL);
const coreAPI = isProduction ? "/api" : configuredCoreAPI || "/api";
const usersAPI = `${coreAPI}/users`;
const ownersAPI = `${coreAPI}/owners`;
const organisationAPI = `${coreAPI}/organizations`;
const subscriptionAPI = `${coreAPI}/subscriptions`;
const gamefilesAPI = isProduction
  ? "/api/gamefiles"
  : process.env.REACT_APP_GAMEFILES_API_URL;
const matchAPI = isProduction ? '/api/match' : process.env.REACT_APP_MATCH_API_URL;

const TOKEN_MAX_AGE_FALLBACK = 900;
const TOKEN_EXPIRES_AT_KEY = "vtkd_token_expires_at";
const TOKEN_LIFETIME_KEY = "vtkd_token_lifetime";
const MIN_REFRESH_LEEWAY_MS = 30_000;
const MAX_REFRESH_LEEWAY_MS = 180_000;
const REFRESH_FAILURE_BACKOFF_MS = 30_000;

let tokenRefreshInFlight: Promise<void> | null = null;
let refreshBackoffUntil = 0;
let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
let foregroundListenersAttached = false;

function persistTokenExpiry(expiresAt: number, lifetimeSeconds: number) {
  try {
    localStorage.setItem(TOKEN_EXPIRES_AT_KEY, String(expiresAt));
    localStorage.setItem(TOKEN_LIFETIME_KEY, String(lifetimeSeconds));
  } catch {
    // Some browsers disable localStorage. The next refresh will ask Core again.
  }
}

function clearTokenExpiry() {
  try {
    localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
    localStorage.removeItem(TOKEN_LIFETIME_KEY);
  } catch {
    // Ignore storage cleanup failures.
  }
}

function readStoredExpiry(): { expiresAt: number; lifetimeSeconds: number } | null {
  try {
    const expiresAt = Number(localStorage.getItem(TOKEN_EXPIRES_AT_KEY));
    const lifetimeSeconds = Number(localStorage.getItem(TOKEN_LIFETIME_KEY));
    if (!Number.isFinite(expiresAt) || expiresAt <= 0) return null;
    return {
      expiresAt,
      lifetimeSeconds:
        Number.isFinite(lifetimeSeconds) && lifetimeSeconds > 0
          ? lifetimeSeconds
          : TOKEN_MAX_AGE_FALLBACK,
    };
  } catch {
    return null;
  }
}

function refreshLeewayMs(lifetimeSeconds: number) {
  return Math.min(
    MAX_REFRESH_LEEWAY_MS,
    Math.max(MIN_REFRESH_LEEWAY_MS, (lifetimeSeconds * 1000) / 3),
  );
}

function isDocumentForeground() {
  return typeof document !== "undefined" && document.visibilityState === "visible";
}

// null = already expired; 0 = refresh now; >0 = wait that many ms.
function msUntilRefresh(): number | null {
  const info = readStoredExpiry();
  if (!info) return 0;
  const now = Date.now();
  if (info.expiresAt <= now) return null;
  return Math.max(0, info.expiresAt - refreshLeewayMs(info.lifetimeSeconds) - now);
}

function clearRefreshTimer() {
  if (refreshTimerId == null) return;
  clearTimeout(refreshTimerId);
  refreshTimerId = null;
}

function onDocumentForegroundChange() {
  if (isDocumentForeground()) {
    scheduleAuthTokenRefreshTimer();
    return;
  }
  clearRefreshTimer();
}

function attachForegroundListeners() {
  if (foregroundListenersAttached || typeof document === "undefined") return;
  document.addEventListener("visibilitychange", onDocumentForegroundChange);
  window.addEventListener("pageshow", onDocumentForegroundChange);
  foregroundListenersAttached = true;
}

function detachForegroundListeners() {
  if (!foregroundListenersAttached || typeof document === "undefined") return;
  document.removeEventListener("visibilitychange", onDocumentForegroundChange);
  window.removeEventListener("pageshow", onDocumentForegroundChange);
  foregroundListenersAttached = false;
}

async function renewAuthToken() {
  const token = getCookie("token");
  if (!token) return;
  try {
    const response = await fetch(`${usersAPI}/token`, {
      method: "GET",
      headers: { Authorization: "Bearer " + token },
    });
    if (response.status === 401 || response.status === 403) {
      deleteCookie("token");
      return;
    }
    if (response.status !== 200) {
      refreshBackoffUntil = Date.now() + REFRESH_FAILURE_BACKOFF_MS;
      return;
    }

    const data = await response.json();
    const nextToken = typeof data.token === "string" ? data.token : data.token?.access_token;
    const expiresIn = Number(data.expires_in ?? data.token?.expires_in);
    if (!nextToken || !Number.isFinite(expiresIn) || expiresIn <= 0) {
      refreshBackoffUntil = Date.now() + REFRESH_FAILURE_BACKOFF_MS;
      return;
    }
    setAuthToken(nextToken, expiresIn);
    refreshBackoffUntil = 0;
  } catch (error) {
    refreshBackoffUntil = Date.now() + REFRESH_FAILURE_BACKOFF_MS;
    console.error("Token refresh failed:", error);
  }
}

function ensureRenewInFlight() {
  if (!tokenRefreshInFlight) {
    tokenRefreshInFlight = renewAuthToken().finally(() => {
      tokenRefreshInFlight = null;
    });
  }
  return tokenRefreshInFlight;
}

async function refreshAuthTokenIfDue() {
  const token = getCookie("token");
  if (!token) return;
  if (Date.now() < refreshBackoffUntil) return;

  const delay = msUntilRefresh();
  if (delay == null) {
    deleteCookie("token");
    return;
  }
  if (delay > 0) return;
  return ensureRenewInFlight();
}

async function runScheduledRefresh() {
  refreshTimerId = null;
  if (!isDocumentForeground()) return;
  await refreshAuthTokenIfDue();
  scheduleAuthTokenRefreshTimer();
}

function scheduleAuthTokenRefreshTimer() {
  clearRefreshTimer();
  if (!isDocumentForeground()) return;

  const token = getCookie("token");
  if (!token) return;

  const now = Date.now();
  if (now < refreshBackoffUntil) {
    refreshTimerId = setTimeout(runScheduledRefresh, refreshBackoffUntil - now);
    return;
  }

  const delay = msUntilRefresh();
  if (delay == null) {
    deleteCookie("token");
    return;
  }
  refreshTimerId = setTimeout(runScheduledRefresh, delay);
}

export function startAuthTokenRefreshTimer() {
  attachForegroundListeners();
  scheduleAuthTokenRefreshTimer();
}

export function stopAuthTokenRefreshTimer() {
  clearRefreshTimer();
  detachForegroundListeners();
}

export function getCookie(name: string) {
  var cookies = document.cookie.split(";");
  for (var i = 0; i < cookies.length; i++) {
    var cookie = cookies[i].trim();

    if (cookie.startsWith(name + "=")) {
      return cookie.substring(name.length + 1);
    }
  }
  return "";
}

export function setAuthToken(token: string, expiresIn?: number) {
  const maxAge =
    typeof expiresIn === "number" && Number.isFinite(expiresIn) && expiresIn > 0
      ? Math.floor(expiresIn)
      : TOKEN_MAX_AGE_FALLBACK;
  persistTokenExpiry(Date.now() + maxAge * 1000, maxAge);
  document.cookie = `token=${token}; max-age=${maxAge}; path=/`;
  startAuthTokenRefreshTimer();
}

export function deleteCookie(name: string) {
  document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  if (name === "token") {
    clearTokenExpiry();
    stopAuthTokenRefreshTimer();
  }
}

export function resetTokenRefreshStateForTests() {
  tokenRefreshInFlight = null;
  refreshBackoffUntil = 0;
  stopAuthTokenRefreshTimer();
  clearTokenExpiry();
}

interface loginObj {
  username?: string;
  email?: string;
  password: string;
  mode: "member" | "owner";
}

interface setPWObj {
  email?: string;
  username?: string;
  password: string;
  new_password: string;
  mode: "member" | "owner";
}

interface createUserObj {
  username: string;
  alias: string;
  role: "User" | "GameMaster";
}

interface updateAliasObj {
  id: string;
  alias: string;
}

interface updateRoleObj {
  id: string;
  role: "GameMaster" | "User";
}

interface deleteUserObj {
  id: string;
  disabled: boolean;
}

interface resetUserObj {
  id: string;
}

interface resetOwnerObj {
  email: string;
}

interface getUsersObj {
  organization: string;
}

interface bulkCreateUserObj {
  organization: string;
  bypass_password_reset: boolean;
  users: {
    user_name: string;
    display_name: string;
    password: string;
    role: string;
  }[]
}

export async function getAuthUser2() {
  await refreshAuthTokenIfDue();
  return fetch(`${coreAPI}/auth/info`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + getCookie("token"),
    },
  });
}

export async function logoutUser() {
  deleteCookie("token");
  return true
}

export async function updateTokenExpiry() {
  return refreshAuthTokenIfDue();
}

export async function loginUser(credentials: loginObj) {
  try {
    const response = await fetch(`${coreAPI}/auth/login-with-credentials`, {
      method: "POST",
      body: JSON.stringify(credentials),
    });

    // Parse the response JSON
    return response;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function setAccountPW(credentials: setPWObj) {
  try {
    const response = await fetch(
      `${credentials.mode === "member" ? usersAPI : ownersAPI}/change-password`,
      {
        method: "POST",
        body: JSON.stringify(credentials),
      }
    );

    // Parse the response JSON
    return response;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function createUser(credentials: createUserObj) {
  try {
    updateTokenExpiry();
    const response = await fetch(`${usersAPI}/create`, {
      method: "POST",
      body: JSON.stringify(credentials),
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
    });

    // Parse the response JSON
    return response;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function getOrgUsers(credentials: getUsersObj) {
  try {
    updateTokenExpiry();
    const response = await fetch(`${usersAPI}/organization`, {
      method: "POST",
      body: JSON.stringify(credentials),
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
    });

    // Parse the response JSON
    return response;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function getOrgList() {
  try {
    updateTokenExpiry();
    const response = await fetch(`${organisationAPI}/token`, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
    });

    // Parse the response JSON
    return response;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function getOrgById(orgID: string) {
  try {
    updateTokenExpiry();
    const response = await fetch(`${organisationAPI}/${orgID}`, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
    });

    // Parse the response JSON
    return response;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function updateUserAlias(credentials: updateAliasObj) {
  try {
    updateTokenExpiry();
    const response = await fetch(`${usersAPI}/update-alias`, {
      method: "POST",
      body: JSON.stringify(credentials),
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
    });

    // Parse the response JSON
    return response;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function updateUserRole(credentials: updateRoleObj) {
  try {
    updateTokenExpiry();
    const response = await fetch(`${usersAPI}/update-role`, {
      method: "POST",
      body: JSON.stringify(credentials),
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
    });

    // Parse the response JSON
    return response;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function deleteUser(credentials: deleteUserObj) {
  try {
    updateTokenExpiry();
    const response = await fetch(`${usersAPI}/update-disabled`, {
      method: "POST",
      body: JSON.stringify(credentials),
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
    });

    // Parse the response JSON
    return response;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function resetUser(credentials: resetUserObj) {
  try {
    updateTokenExpiry();
    const response = await fetch(`${usersAPI}/reset-password`, {
      method: "POST",
      body: JSON.stringify(credentials),
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
    });

    // Parse the response JSON
    return response;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function resetOwner(credentials: resetOwnerObj) {
  try {
    updateTokenExpiry();
    const response = await fetch(`${ownersAPI}/reset-password`, {
      method: "POST",
      body: JSON.stringify(credentials),
    });

    // Parse the response JSON
    return response;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function getSubscriptionLink(orgID: string) {
  try {
    updateTokenExpiry();
    const res = await fetch(`${subscriptionAPI}/billing_portal/${orgID}`, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
    });

    // Parse the response JSON
    return res;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function getSubscrtiptionData(orgID: string) {
  try {
    updateTokenExpiry();
    const res = await fetch(`${subscriptionAPI}/check/${orgID}`, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
    });

    // Parse the response JSON
    return res;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function getAllGameFiles(orgID: string | undefined) {
  try {
    updateTokenExpiry();
    const res = await fetch(`${gamefilesAPI}/list?org_id=${orgID}`, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
    });

    // Parse the response JSON
    return res;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function downloadGameFile(
  key: string,
  signal?: AbortSignal,
  rangeStart?: number,
  expectedETag?: string,
) {
  try {
    updateTokenExpiry();
    const headers: Record<string, string> = {
      Authorization: "Bearer " + getCookie("token"),
    };
    if (rangeStart !== undefined) {
      headers.Range = `bytes=${rangeStart}-`;
    }
    if (expectedETag) {
      headers["If-Match"] = expectedETag;
    }

    const res = await fetch(
      `${gamefilesAPI}/download?key=${encodeURIComponent(key)}`,
      {
        method: "GET",
        signal,
        headers,
      }
    );

    // Parse the response JSON
    return res;

    // Work with the parsed JSON data
  } catch (error: any) {
    if (error?.name === "AbortError") {
      throw error;
    }
    // Downloads need the original network error so the caller can preserve
    // its file session and automatically resume when connectivity returns.
    console.error("Fetch error:", typeof error, error);
    throw error;
  }
}

export function getBulkUserCreatePreviewUrl(organization_id: string | undefined) {
  return `${usersAPI}/bulk-create-preview?organization_id=` + organization_id
}

export async function bulkCreateUser(credentials: bulkCreateUserObj) {
  try {
    updateTokenExpiry();
    const response = await fetch(`${usersAPI}/bulk-create`, {
      method: "POST",
      body: JSON.stringify(credentials),
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
    });

    // Parse the response JSON
    return response;

    // Work with the parsed JSON data
  } catch (error: any) {
    // Handle any errors that occurred during the fetch
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}



export interface matchActivityInfoObj {
  organization_id?: string;
  name: string;
  description: string;
  match_type: string;
  display_policy: string;
  start_at: string;
  end_at: string;
  allow_register: boolean;
  match_tz: number;
  max_court_count: number;
  options: object;
}

export interface updateMatchActivityInfoObj {
  name: string;
  description: string;
  display_policy: string;
  allow_register: boolean;
  logo: string;
}

export async function createMatchActivity(credentials: matchActivityInfoObj) {
  return requestAPI(`${matchAPI}/operator/activity/new`, 'POST', credentials)
}

export async function updateMatchActivity(id: string, credentials: updateMatchActivityInfoObj) {
  return requestAPI(`${matchAPI}/operator/activity/${id}/update-info`, 'POST', credentials)
}

export async function deleteMatchActivity(id: string, name: string) {
  return requestAPI(`${matchAPI}/operator/activity/${id}/delete-all`, 'POST', {name})
}

export async function getMatchActivityInfo(id: string) {
  return requestAPI(`${matchAPI}/operator/activity/${id}/info`, 'GET', false)
}

export async function getMatchActivityParticipants(id: string) {
  return requestAPI(`${matchAPI}/operator/activity/${id}/participant`, 'GET', false)
}

export function getParseMatchActivityImportUrl(id: string) {
  return `${matchAPI}/operator/activity/${id}/parseImport`;
}

export async function importMatchActivity(id: string, data: any) {
  return requestAPI(`${matchAPI}/operator/activity/${id}/import`, 'POST', data)
}

export async function batchUpdateActivityMatchCourt(activityId: string, matchIds: string[], court: number) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/update-match-court`, 'POST', {
    match_ids: matchIds,
    court
  })
}

export interface matchActivityParticipantObj {
  team_name: string;
  display_name: string;
  first_name: string;
  last_name: string;
  birth_date: string;
  gender: string;
  license_number: string;
  location: string;
  noc: string;
  category_ids: number[];
}


export async function addMatchActivityParticipant(activityId: string, credentials: matchActivityParticipantObj) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/add-participant`, 'POST', credentials)
}

export async function updateMatchActivityParticipant(activityId: string, id: string, credentials: matchActivityParticipantObj) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/update-participant`, 'POST', {id, ...credentials})
}

export async function deleteMatchActivityParticipant(activityId: string, id: string) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/delete-participant`, 'POST', {id})
}

export async function generateMatchBracket(activityId: string, category_id: string) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/generate-bracket`, 'POST', {category_id})
}

export async function getBracketDraft(activityId: string, categoryId: string) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/bracket-draft?category_id=${categoryId}`, 'GET', false)
}

export async function generateBracketDraft(
  activityId: string,
  categoryId: string,
  expectedRevision: number,
  randomizeMode?: 'unlocked' | 'unassigned'
) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/bracket-draft/generate`, 'POST', {
    category_id: Number(categoryId),
    expected_revision: expectedRevision,
    ...(randomizeMode ? { randomize_mode: randomizeMode } : {}),
  })
}

export async function updateBracketDraft(
  activityId: string,
  categoryId: string,
  expectedRevision: number,
  assignments: {slot_key: string; participant_id: string; is_seed_locked: boolean}[]
) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/bracket-draft/update`, 'POST', {
    category_id: Number(categoryId),
    expected_revision: expectedRevision,
    assignments,
  })
}

export async function exportBracketDraftXLSX(
  activityId: string,
  categoryId: string
) {
  await refreshAuthTokenIfDue();
  return fetch(
    `${matchAPI}/operator/activity/${activityId}/bracket-draft/export?category_id=${categoryId}`,
    {
      method: 'GET',
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
    }
  )
}

export async function importBracketDraftXLSX(
  activityId: string,
  categoryId: string,
  expectedRevision: number,
  file: File,
  options?: {dryRun?: boolean; mode?: 'full' | 'partial'}
) {
  const body = new FormData()
  body.append('category_id', categoryId)
  body.append('expected_revision', String(expectedRevision))
  body.append('file', file)
  if (options?.dryRun) {
    body.append('dry_run', 'true')
  }
  if (options?.mode) {
    body.append('mode', options.mode)
  }

  await refreshAuthTokenIfDue();
  return fetch(
    `${matchAPI}/operator/activity/${activityId}/bracket-draft/import`,
    {
      method: 'POST',
      headers: {
        Authorization: "Bearer " + getCookie("token"),
      },
      body,
    }
  )
}

export async function publishBracketDraft(activityId: string, categoryId: string, expectedRevision: number) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/bracket-draft/publish`, 'POST', {
    category_id: Number(categoryId),
    expected_revision: expectedRevision,
  })
}

export async function resetBracketDraft(activityId: string, categoryId: string, expectedRevision: number) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/bracket-draft/reset`, 'POST', {
    category_id: Number(categoryId),
    expected_revision: expectedRevision,
  })
}

export async function getMatchBracket(activityId: string, category_id: string) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/matches?cat=${category_id}`, 'GET', false)
}

export async function getActivitySchedule(activityId: string) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/schedule`, 'GET', false)
}

export async function updateActivitySchedule(activityId: string, items: any) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/update-schedule`, 'POST', {items})
}


export async function downloadActivitySchedulePdf(activityId: string, params: any) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/download-schedule-pdf`, 'POST', params)
}

export async function downloadActivityBracketPdf(activityId: string, params: any) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/download-bracket-pdf`, 'POST', params)
}

export interface matchActivityCategoryObj {
  id: number;
  name: string;
  bracket_adjustment_mode: string;
  round_names: string[];
  options: any
}

export async function updateMatchActivityCategory(activityId: string, credentials: matchActivityCategoryObj) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/update-category`, 'POST', credentials)
}

export async function reloadMatchActivityCategory(activityId: string, category_id: number) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/reload-category`, 'POST', {category_id})
}

export async function deleteMatchActivityCategory(activityId: string, id: number) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/delete-category`, 'POST', {id})
}

export async function getMatchActivityRoundResult(activityId: string, id: string, all: boolean) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/match-results?match_id=${id}&all=${all ? 1 : 0}`, 'GET', null)
}

export async function resetActivityGameMatch(activityId: string, match_id: string, all: boolean) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/reset-match`, 'POST', {match_id, all})
}

export async function updateActivityGameMatch(activityId: string, values: any) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/update-match`, 'POST', values)
}


export async function updateActivityGameMatchReason(activityId: string, match_id: string, reason: string) {
  return requestAPI(`${matchAPI}/operator/activity/${activityId}/update-match-reason`, 'POST', {match_id, reason})
}

export async function getMatchActivitySelfList(org_id: string, all: boolean) {
  return requestAPI(`${matchAPI}/operator/activity/list?org_id=${org_id}&all=${all ? 1 : 0}`, 'GET', false)
}

export function getActivityMatchImportTemplate() {
  return `${matchAPI}/operator/activity/download-template`
}

async function requestAPI(apiUrl: string, method: 'GET' | 'POST', body: any) {
  await refreshAuthTokenIfDue();
  let options :RequestInit = {
    method: method,
    headers: {
      Authorization: "Bearer " + getCookie("token"),
    },
  }

  if (body) options.body = JSON.stringify(body);

  try {
    const response = await fetch(apiUrl, options);
    if (response.status === 401) {
      deleteCookie("token");
    }
    return response;
  } catch (error: any) {
    console.error("Fetch error:", typeof error, error);
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { "Content-Type": "application/json" },
    });
  }
}
